/**
 * TGColumnConfigurator
 * Popover with checkboxes for column visibility
 * Enhanced with keyboard navigation, accessibility, and drag-reorder
 */

import React, { useRef, useState } from 'react';
import { Button, Popover, Checkbox, Space, Divider } from 'antd';
import { SettingOutlined, DragOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { spacing } from '../../../theme/designTokens';

const DRAG_TYPE = 'COLUMN_ITEM';

/**
 * Draggable Column Item
 */
function DraggableColumnItem({
  col,
  index,
  checked,
  onToggle,
  onMove,
  focusedIndex,
  onKeyDown,
  itemRef,
}) {
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: DRAG_TYPE,
    item: { index, key: col.key },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const [, drop] = useDrop(() => ({
    accept: DRAG_TYPE,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        onMove(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  }));

  return (
    <div
      ref={(node) => {
        drag(drop(node));
        if (itemRef) itemRef(node);
      }}
      role="menuitemcheckbox"
      aria-checked={checked}
      tabIndex={focusedIndex === index ? 0 : -1}
      onKeyDown={onKeyDown}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
        padding: `${spacing.xs}px 0`,
      }}
    >
      <DragOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />
      <Checkbox
        checked={checked}
        onChange={() => onToggle(col.key)}
        aria-label={`Toggle ${col.title} column visibility`}
        style={{ flex: 1 }}
      >
        {col.title}
      </Checkbox>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Array} props.columns - All columns
 * @param {string[]} props.visibleColumns - Currently visible column keys
 * @param {string[]} [props.columnOrder] - Column order (keys)
 * @param {Function} props.onChange - (keys: string[]) => void
 * @param {Function} [props.onReorder] - (fromIndex, toIndex) => void
 * @param {Function} props.onReset - Reset to defaults
 * @param {boolean} [props.enableDragReorder] - Enable drag-reorder (default: true)
 */
function TGColumnConfigurator({
  columns,
  visibleColumns,
  columnOrder = [],
  onChange,
  onReorder,
  onReset,
  enableDragReorder = true,
}) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const checkboxRefs = useRef({});

  // Order columns based on columnOrder if provided
  const orderedColumns = React.useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;

    const orderMap = new Map(columnOrder.map((key, idx) => [key, idx]));
    return [...columns].sort((a, b) => {
      const aOrder = orderMap.has(a.key) ? orderMap.get(a.key) : 999;
      const bOrder = orderMap.has(b.key) ? orderMap.get(b.key) : 999;
      return aOrder - bOrder;
    });
  }, [columns, columnOrder]);

  const handleToggle = (columnKey) => {
    const newVisible = visibleColumns.includes(columnKey)
      ? visibleColumns.filter((k) => k !== columnKey)
      : [...visibleColumns, columnKey];
    onChange(newVisible);
  };

  const handleMove = (fromIndex, toIndex) => {
    if (onReorder) {
      onReorder(fromIndex, toIndex);
    }
  };

  const handleSelectAll = () => {
    onChange(columns.map((col) => col.key));
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  const allSelected = visibleColumns.length === columns.length;
  const someSelected = visibleColumns.length > 0 && visibleColumns.length < columns.length;

  // Keyboard navigation handler
  const handleKeyDown = (e, columnKey, index) => {
    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        handleToggle(columnKey);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (index < orderedColumns.length - 1) {
          setFocusedIndex(index + 1);
          checkboxRefs.current[orderedColumns[index + 1].key]?.focus();
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (index > 0) {
          setFocusedIndex(index - 1);
          checkboxRefs.current[orderedColumns[index - 1].key]?.focus();
        }
        break;

      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;

      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        checkboxRefs.current[orderedColumns[0]?.key]?.focus();
        break;

      case 'End':
        e.preventDefault();
        const lastIndex = orderedColumns.length - 1;
        setFocusedIndex(lastIndex);
        checkboxRefs.current[orderedColumns[lastIndex]?.key]?.focus();
        break;

      default:
        break;
    }
  };

  const content = (
    <div
      style={{ width: 280 }}
      role="menu"
      aria-label="Column visibility settings"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.sm,
        }}
      >
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => {
            if (e.target.checked) {
              handleSelectAll();
            } else {
              handleDeselectAll();
            }
          }}
          aria-label="Toggle all columns"
        >
          <strong>Select All</strong>
        </Checkbox>
        <Button
          type="link"
          size="small"
          onClick={onReset}
          aria-label="Reset column visibility to defaults"
        >
          Reset
        </Button>
      </div>

      <Divider style={{ margin: `${spacing.xs}px 0` }} />

      <div
        role="group"
        aria-label="Column list"
        style={{ minHeight: 200, maxHeight: 400, overflowY: 'auto' }}
      >
        {enableDragReorder && orderedColumns.length > 0 ? (
          <DndProvider backend={HTML5Backend}>
            {orderedColumns.map((col, index) => (
              <DraggableColumnItem
                key={col.key}
                col={col}
                index={index}
                checked={visibleColumns.includes(col.key)}
                onToggle={handleToggle}
                onMove={handleMove}
                focusedIndex={focusedIndex}
                onKeyDown={(e) => handleKeyDown(e, col.key, index)}
                itemRef={(el) => (checkboxRefs.current[col.key] = el)}
              />
            ))}
          </DndProvider>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {orderedColumns.map((col, index) => (
              <div
                key={col.key}
                role="menuitemcheckbox"
                aria-checked={visibleColumns.includes(col.key)}
                tabIndex={focusedIndex === index ? 0 : -1}
                ref={(el) => (checkboxRefs.current[col.key] = el)}
                onKeyDown={(e) => handleKeyDown(e, col.key, index)}
                style={{ outline: 'none' }}
              >
                <Checkbox
                  checked={visibleColumns.includes(col.key)}
                  onChange={() => handleToggle(col.key)}
                  aria-label={`Toggle ${col.title} column visibility`}
                >
                  {col.title}
                </Checkbox>
              </div>
            ))}
          </Space>
        )}
      </div>

      <div
        style={{
          marginTop: spacing.sm,
          fontSize: 11,
          color: 'rgba(0,0,0,0.45)',
          fontStyle: 'italic',
        }}
      >
        {enableDragReorder && 'Drag to reorder • '}↑↓ arrows to navigate • Space/Enter to toggle
        • Esc to close
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title="Columns"
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(visible) => {
        setOpen(visible);
        if (visible) {
          setFocusedIndex(0);
          // Focus first checkbox when opening
          setTimeout(() => {
            checkboxRefs.current[orderedColumns[0]?.key]?.focus();
          }, 100);
        }
      }}
    >
      <Button
        icon={<SettingOutlined />}
        aria-label="Configure column visibility"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Columns
      </Button>
    </Popover>
  );
}

export default TGColumnConfigurator;
